import React, { useCallback, useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Button, Checkbox, Dropdown, Label, Loader, Modal, Popup } from "semantic-ui-react"
import SearchParams from "simplydo/core/searchParams"
import styled from "styled-components"

import api, { asQuery } from "api"
import { ChallengeFollowButton } from "components/lib/FollowButton"
import { Sticky } from "components/lib/UI"
import { useAppSelector } from "store"
import useTheme from "theme/useTheme"
import util from "utils/utils"

import AdjustableUserList from "./AdjustableUserList"
import ChallengeStageChanger from "./ChallengeStageChanger"

const StyledStatusBar = styled.div`
  height: ${({ theme }) => (!theme.sizes.isComputer ? 50 : 60)}px;
  padding: ${({ theme }) => (!theme.sizes.isComputer ? "10px 10px" : "10px 20px")};
  vertical-align: middle;
  background-image: ${(props) => `linear-gradient(to bottom right, ${props.theme.secondaryColour}, ${util.adjustColour(props.theme.secondaryColour, 0.5)})`};
  top: 51px;
  width: 100%;
  z-index: 90;
  display: flex;
  align-items: center;
  justify-content: space-between;
  > div {
    display: flex;
    align-items: center;
  }
`

const ChallengeStatusBar = ({ challengeId, completed, loading, newIdea, myIdeas, overviewPage, redirectToLogin }) => {
  const user = useAppSelector((state) => state.user)
  const { t } = useTranslation()
  const challengeQuery = useQuery({
    queryKey: ["challenges", challengeId],
    queryFn: asQuery(api.challenges.get, {
      params: [challengeId],
    }),
  })
  const challenge = challengeQuery.data || {}

  const [template, setTemplate] = useState({})
  const [requestToOpenChallengeOpen, setRequestToOpenChallengeOpen] = useState(false)
  const [selectedRequestAdmin, setSelectedRequestAdmin] = useState("")
  const [requestSent, setRequestSent] = useState(false)
  const [adminsLoading, setAdminsLoading] = useState(false)
  const [admins, setAdmins] = useState([])
  const [contactingAdmin, setContactingAdmin] = useState(false)
  const [announcements, setAnnouncements] = useState([])
  const theme = useTheme()

  const params = new SearchParams(window.location.search)
  const shouldOpenChannel = params.get("shouldOpenChallenge", false)

  const canManageChallenge = useMemo(() => util.canManageChallenge(user, challenge), [user, challenge])
  const canEditSettings = useMemo(
    () => util.hasPermission(user, "challenge.editSettings", challengeId),
    [user, challengeId],
  )

  const getChallenge = useCallback(() => {
    if (canManageChallenge || canEditSettings) {
      api.announcements.get(
        "challenge",
        challengeId,
        1,
        (data) => setAnnouncements(data.automaticAnnouncements),
        () => {},
      )
    }
    api.challenges.getTemplate(challengeId, (newTemplate) => setTemplate(newTemplate))
  }, [challengeId, canManageChallenge, canEditSettings])

  useEffect(() => getChallenge(), [getChallenge])

  const userOrg = user?.ownerOrganisation._id
  const getAdmins = useCallback(() => {
    if (userOrg) {
      setAdminsLoading(true)
      api.organisations.getAdmins(
        userOrg,
        { limit: 10, permissions: ["org.publishChallenges"] },
        ({ users }) => {
          setAdmins(users)
          setAdminsLoading(false)
        },
        (err) => {
          toast.error(err.message)
          setAdminsLoading(false)
        },
      )
    }
  }, [userOrg])

  const contactAdmin = useCallback(() => {
    setContactingAdmin(true)
    api.organisations.contactAdmin(
      userOrg,
      { challenge: challengeId, contactType: "requestOpenChallenge", admin: selectedRequestAdmin },
      () => {
        toast.success("Request sent to admin")
        setSelectedRequestAdmin("")
        setContactingAdmin(false)
        setRequestToOpenChallengeOpen(false)
        setRequestSent(true)
      },
      (err) => {
        toast.error(err.message)
        setContactingAdmin(false)
      },
    )
  }, [selectedRequestAdmin, userOrg, challengeId])

  useEffect(() => {
    if (requestToOpenChallengeOpen) {
      getAdmins()
    }
  }, [requestToOpenChallengeOpen, getAdmins])

  const updateStage = useCallback(
    (newStage, openWithComms = false) => {
      const updateStageFunction = () =>
        api.challenges.updateStage(challengeId, newStage, openWithComms, (s) => {
          api.queryClient.setQueryData(["challenges", challengeId], (prevChallenge) => ({
            ...(prevChallenge || {}),
            ...s,
          }))
        })

      if (newStage === "closed") {
        util
          .confirm(
            `Really close this ${t("generic.challenge")}?`,
            `Are you sure you want to close this ${t("generic.challenge")}. Closing the ${t("generic.challenge")} will prevent any new ${t("generic.ideas")} or submissions.`,
          )
          .then(() => {
            updateStageFunction()
          })
          .catch(() => {})
      } else updateStageFunction()
    },
    [challengeId, t],
  )

  const pinChallenge = useCallback(() => {
    const { isPinned, _id } = challenge
    api.challenges.setStatus(_id, { isPinned: !isPinned }, () => {
      api.queryClient.setQueryData(["challenges", _id], (prevChallenge) => ({
        ...(prevChallenge || {}),
        isPinned: !isPinned,
      }))
    })
  }, [challenge])

  const challengeStage = challenge.stage
  useEffect(() => {
    if (shouldOpenChannel && challengeStage !== "published") {
      updateStage("published")
    }
  }, [shouldOpenChannel, challengeStage, updateStage])

  const canPublishChallenges = useMemo(
    () => util.hasPermission(user, "org.publishChallenges", user?.ownerOrganisation?._id),
    [user],
  )
  const canMakeIdea = util.canCreateIdea(user, challenge)
  const myUnsubmittedIdeas = useMemo(() => myIdeas?.filter((idea) => !idea.isSubmitted), [myIdeas])

  return (
    <Sticky top={50} style={{ zIndex: 90 }}>
      <StyledStatusBar theme={theme}>
        <Modal
          mountNode={document.getElementById("semantic-modal-mount-node")}
          open={requestToOpenChallengeOpen}
          onClose={() => setRequestToOpenChallengeOpen(false)}
        >
          <Modal.Header>Request to open {t("generic.challenge")}</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              <p>
                You do not have permission to open {t("generic.challenges")} in your organisation. Please select an
                admin, who will receive a request from you to open this {t("generic.challenge")}.
              </p>
            </Modal.Description>
            {adminsLoading ? (
              <Loader active />
            ) : (
              <div>
                <AdjustableUserList
                  results={admins}
                  actions={(adminUser) => (
                    <Checkbox
                      checked={selectedRequestAdmin === adminUser._id}
                      onClick={() => {
                        setSelectedRequestAdmin((prevId) => (prevId === adminUser._id ? "" : adminUser._id))
                      }}
                      size="large"
                    />
                  )}
                />
              </div>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button content="Cancel" icon="close" onClick={() => setRequestToOpenChallengeOpen(false)} />
            <Button
              color="green"
              icon="check"
              content="Send request"
              disabled={!selectedRequestAdmin}
              loading={contactingAdmin}
              onClick={contactAdmin}
            />
          </Modal.Actions>
        </Modal>

        <div style={{ gap: 4 }}>
          {theme.sizes.isComputer ? (
            <>
              {!challenge.stage && <Label color="yellow">{t("challenge.stages.draft.title")}</Label>}
              {challenge.stage === "published" && (
                <Label color="green" style={{ color: "#fff", marginRight: 18 }}>
                  {t("challenge.stages.published.title")}
                </Label>
              )}
              {challenge.stage === "closed" && (
                <Label color="red" style={{ color: "#fff", marginRight: 18 }}>
                  {t("generic.closed")}
                </Label>
              )}
            </>
          ) : null}

          {theme.sizes.isComputer &&
          (canManageChallenge || util.hasPermission(user, "challenge.viewSettings", challengeId)) ? (
            <React.Fragment>
              <Button size="tiny" as="div" labelPosition="left">
                <Label basic>{challenge.ideaCount || 0}</Label>
                <Button
                  size="tiny"
                  as={Link}
                  to={`/challenges/${challenge._id}/ideas`}
                  icon="lightbulb"
                  content={t("common:capitalise", { key: "generic.ideas" })}
                  inverted
                  basic
                />
              </Button>

              <Button size="tiny" as="div" labelPosition="left">
                <Label basic>{challenge.audienceSize || 0}</Label>
                <Button
                  size="tiny"
                  as={Link}
                  inverted
                  basic
                  to={`/challenges/${challenge._id}/settings/audience`}
                  icon="users"
                  content={t("challenge.settings.audience.title")}
                />
              </Button>
            </React.Fragment>
          ) : null}

          {util.hasPermission(user, "org.manageChallenges", user?.ownerOrganisation?._id) && !theme.sizes.isMobile ? (
            <Popup
              content={t("challenges.pin.info")}
              on="hover"
              trigger={
                <Button
                  size="tiny"
                  inverted
                  basic
                  content={
                    challenge.isPinned ? t("challenge.ideas.actions.pin.unPin") : t("challenge.ideas.actions.pin.pin")
                  }
                  icon="thumbtack"
                  onClick={pinChallenge}
                />
              }
            />
          ) : null}

          <ChallengeFollowButton challenge={challenge} />

          {!canManageChallenge &&
          !util.hasPermission(user, "challenge.viewSettings", challengeId) &&
          theme.sizes.isComputer ? (
            <span style={{ color: "white", fontSize: 16, marginLeft: 10 }}>{challenge.name}</span>
          ) : null}
        </div>
        {loading ? (
          <div />
        ) : (
          <div style={{ gap: 4 }}>
            {canManageChallenge || util.hasPermission(user, "challenge.viewSettings", challengeId) ? (
              !theme?.sizes?.isMobile ? (
                <React.Fragment>
                  {window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/settings/) && (
                    <Button
                      size="tiny"
                      content={t("challenge.settings.viewChallenge")}
                      as={Link}
                      to={`/challenges/${challenge._id}`}
                      icon="arrow left"
                      inverted
                      basic
                    />
                  )}
                  {!window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/settings/) && (
                    <Button
                      size="tiny"
                      icon="cogs"
                      inverted
                      basic
                      content={t("challenge.settings.title")}
                      as={Link}
                      to={`/challenges/${challenge._id}/settings`}
                    />
                  )}
                  {!window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/format/) &&
                    (canManageChallenge || util.hasPermission(user, "challenge.editIdeaFormat", challengeId)) && (
                      <Button
                        size="tiny"
                        icon="pencil"
                        inverted
                        basic
                        content={t("challenge.editFormat")}
                        as={Link}
                        to={`/challenges/${challenge._id}/settings/format`}
                      />
                    )}
                  {canManageChallenge || util.hasPermission(user, "challenge.editSettings", challengeId) ? (
                    canPublishChallenges ? (
                      <ChallengeStageChanger
                        challengeId={challengeId}
                        updateStage={updateStage}
                        completed={completed}
                        announcements={announcements}
                        template={template}
                      />
                    ) : challenge.stage !== "published" ? (
                      <Button
                        size="tiny"
                        primary
                        disabled={requestSent}
                        icon={requestSent ? "check" : undefined}
                        content={t(`challenge.stages.draft.request${requestSent ? "Sent" : ""}`)}
                        onClick={() => setRequestToOpenChallengeOpen(true)}
                      />
                    ) : null
                  ) : null}
                </React.Fragment>
              ) : (
                <Dropdown icon={null} direction="left" trigger={<Button basic inverted icon="cogs" size="tiny" />}>
                  <Dropdown.Menu>
                    {!window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/format/) &&
                      (canManageChallenge || util.hasPermission(user, "challenge.editIdeaFormat", challengeId)) && (
                        <Dropdown.Item
                          icon="pencil"
                          content={t("challenge.editFormat")}
                          as={Link}
                          to={`/challenges/${challenge._id}/settings/format`}
                        />
                      )}
                    {window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/settings/) && (
                      <Dropdown.Item
                        icon="arrow left"
                        content={t("challenge.settings.viewChallenge")}
                        as={Link}
                        to={`/challenges/${challenge._id}`}
                      />
                    )}
                    {!window.location.pathname.match(/\/challenges\/[a-zA-Z0-9-]+\/settings/) && (
                      <Dropdown.Item
                        icon="cogs"
                        content={t("challenge.settings.title")}
                        as={Link}
                        to={`/challenges/${challenge._id}/settings`}
                      />
                    )}
                    {canManageChallenge || util.hasPermission(user, "challenge.editSettings", challengeId) ? (
                      <Dropdown.Item
                        icon="thumbtack"
                        content={
                          challenge.isPinned ? `Un-pin ${t("generic.challenge")}` : `Pin ${t("generic.challenge")}`
                        }
                        onClick={pinChallenge}
                      />
                    ) : null}
                    {canPublishChallenges ? (
                      <>
                        {!challenge.stage && (
                          <Dropdown.Item
                            content={t("challenge.stages.draft.open")}
                            onClick={() => updateStage("published")}
                          />
                        )}
                        {challenge.stage === "published" && (
                          <Dropdown.Item
                            content={t("challenge.stages.published.close")}
                            onClick={() => updateStage("closed")}
                          />
                        )}
                        {challenge.stage === "closed" && (
                          <Dropdown.Item
                            content={t("challenge.stages.closed.reOpen")}
                            onClick={() => updateStage("published")}
                          />
                        )}
                      </>
                    ) : null}
                  </Dropdown.Menu>
                </Dropdown>
              )
            ) : (
              !theme?.sizes?.isMobile &&
              overviewPage && (
                <div>
                  {challenge?.stage === "published" && !user ? (
                    <>
                      {(!myUnsubmittedIdeas || myUnsubmittedIdeas?.length === 0) && canMakeIdea ? (
                        <Button
                          size="tiny"
                          primary
                          icon="plus"
                          content={
                            myIdeas?.length === 0
                              ? challenge.buttonText
                                ? challenge.buttonText
                                : t("challenge.ideas.createYourIdea")
                              : t("challenge.startNew")
                          }
                          onClick={canMakeIdea ? newIdea : () => redirectToLogin()} // If public idea creation is enabled a user does not need to be logged in
                        />
                      ) : null}

                      {myUnsubmittedIdeas?.length === 1 && (
                        <Button
                          size="tiny"
                          icon="lightbulb"
                          content={t("challenge.continueIdea")}
                          primary
                          as={Link}
                          to={`/ideas/${myUnsubmittedIdeas[0]._id}`}
                        />
                      )}

                      {myUnsubmittedIdeas?.length > 1 && (
                        <Dropdown
                          icon={null}
                          trigger={
                            <Button
                              size="tiny"
                              style={{ width: "100%" }}
                              primary
                              icon="lightbulb"
                              content={t("challenge.continueIdea")}
                            />
                          }
                        >
                          <Dropdown.Menu>
                            {myUnsubmittedIdeas.map((i) => (
                              <Dropdown.Item key={i._id} as={Link} to={`/ideas/${i._id}`} content={i.name} />
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </>
                  ) : null}
                </div>
              )
            )}
          </div>
        )}
      </StyledStatusBar>
    </Sticky>
  )
}

export default ChallengeStatusBar
